import React, { useContext, Suspense, useEffect } from 'react'
import { PageData, Wrapper } from './types'
import pageResolver from './resolve-page'
import Title from '../components/title'
import { UsersLayout, AdminsLayout, Layout } from '../../../components/layouts'
import { applicationConfiguration } from '../context/configuration-context'
import PageLoader from '../components/loader'
import { logger } from 'app/components/logger'
import { routeState } from '..'
import { GoToLocaionHash } from 'app/components/hash-link'
import { GetBackAfterSignin } from './back-after-signin'
import { eventsHandler } from '../events/queue'
import { Event } from '../events/events'
import { MainNotification } from 'app/components/main-notification'
import { AuthContext } from 'app/modules/auth'
import { pushPageView } from '../helpers/telemetry-functions'
import { traceOtelPageView } from '../context/monitoring/otel'
import { identifyClarityPage } from '../context/monitoring/clarity'
import { useRouteMatch } from '../hooks/use-route-match'

export function RenderedRouteWrapper({
  routeProps,
  page,
}: {
  routeProps: { [key: string]: any }
  page: PageData
}): JSX.Element {
  const config = useContext(applicationConfiguration)
  eventsHandler.trigger(Event.PageLoad, page.key)

  if (config.firstRun && ['first-sign-up', 'first-signup-redirect', 'admin-setup'].indexOf(page.key as string) === -1) {
    return pageResolver.redirectTo('first-sign-up')
  }

  const resolveResult = pageResolver.resolve(page.key as string)
  logger.debug('Page resolved to ', resolveResult)

  const PageComponent = preparePageComponent(resolveResult as CallableFunction, routeProps, page)
  const LayoutComponent = page.layout === Layout.Admins ? AdminsLayout : UsersLayout
  const Wrapper: Wrapper | undefined = resolveResult === GetBackAfterSignin ? undefined : page.wrapper

  const content = Wrapper ? (
    <Wrapper {...routeProps}>
      <PageComponent />
    </Wrapper>
  ) : (
    <PageComponent />
  )

  return (
    <LayoutComponent>
      <MainNotification />
      <Suspense fallback={<PageLoader />}>{content}</Suspense>
    </LayoutComponent>
  )
}

/**
 * Redirect-only pages and invisible handshakes should not trigger page views
 */
function shouldSkipPageViewTracking(pageKey: string): boolean {
  const skipList = [
    'first-signup-redirect',
    'signup-redirect',
    'invite-signup-redirect',
    'report-overview-redirect',
    'report-new-overview-redirect',
    'trends-redirect',
    'admin-home',
    'admin-user-management-home',
    'admin-scan-sources-home',
    'admin-settings-home',
    'admin-stat-home',
    'admin-jobs-home',
    'admin-audit-logger-home',
  ]
  return skipList.includes(pageKey)
}

function preparePageComponent(resolveResult: CallableFunction, routeProps: { [key: string]: any }, page: PageData) {
  const PageComponent = resolveResult

  function FullPageComponent() {
    const pageState = useContext(routeState)
    const auth = useContext(AuthContext)
    const routeTemplate = useRouteMatch()

    useEffect(() => {
      if (pageState.data.page !== page) {
        pageState.setValues({ page: page })
      }

      // Wait for page to stabilize before tracking (handles rapid redirects)
      const timeoutId = setTimeout(() => {
        if (!shouldSkipPageViewTracking(page.key as string)) {
          const userId = auth?.user?.accountId
          pushPageView(routeTemplate, userId)
          traceOtelPageView(routeTemplate, window.location.pathname, userId)
          identifyClarityPage(userId, routeTemplate)
        }
      }, 150)

      return () => clearTimeout(timeoutId)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page.key])

    return (
      <>
        <GoToLocaionHash />
        <Title title={page.title} />
        {/*@ts-expect-error CallableFunction as JSX component*/}
        <PageComponent {...routeProps} page={page} />
      </>
    )
  }

  return FullPageComponent
}
